import Image from "@components/Image";
import Icons from "@gstyles/styleguide/icons";
import useSwiper from "@hooks/useSwiper";
import cx from "classnames";
import { useState } from "react";
import "swiper/css/scrollbar";
import styles from "./ImagesScrollbarGallery.module.scss";

const formatPaginationNumber = (
  locales?: string,
  options?: Intl.NumberFormatOptions
) => {
  return (value: number) =>
    new Intl.NumberFormat(
      locales ?? "de-DE",
      options ?? { minimumIntegerDigits: 2 }
    ).format(value);
};
interface Props {
  isAuto?: boolean;
  interval?: number;
  galleryName: string;
  images: { src: string; alt: string }[];
  itemClassName?: string | { [key: string]: string };
  imageClassName?: string | { [key: string]: string };
}

export default function ImagesScrollbarGallery({
  isAuto = false,
  interval = 3000,
  images = [],
  galleryName,
  itemClassName,
  imageClassName,
}: Props) {
  const [activeIndex, $activeIndex] = useState(0);
  const formatFractionNumber = formatPaginationNumber();
  useSwiper(`.${galleryName}`, {
    effect: "coverflow",
    spaceBetween: 32,
    grabCursor: true,
    centeredSlides: true,
    slidesPerView: "auto",
    coverflowEffect: {
      rotate: 0,
      stretch: -180,
      depth: 300,
      modifier: 1,
      slideShadows: false,
    },
    rewind: true,
    scrollbar: {
      el: `.swiper-scrollbar`,
      hide: false,
      draggable: true,
    },
    autoplay: !!isAuto && {
      delay: interval,
      disableOnInteraction: false,
    },
    navigation: {
      nextEl: `.swiper-${galleryName}-next`,
      prevEl: `.swiper-${galleryName}-prev`,
    },
    on: {
      slideChange: (swiper) => {
        if (swiper) {
          $activeIndex(swiper.realIndex);
        }
      },
    },
  });
  return (
    <div className={cx(styles.section)}>
      <div className={cx(styles.swiperBox)}>
        <div className={cx(styles.swiper, `${galleryName}`, "swiper")}>
          <Icons
            name="chevron-left-outline"
            color="#F7EEDF"
            className={cx(`swiper-${galleryName}-prev`, styles.prevChevronIcon)}
          />
          <div className={cx("swiper-wrapper")}>
            {images.map((img, index) => {
              return (
                <div
                  key={`${index}_${img.alt}`}
                  className={cx("swiper-slide", styles.item, itemClassName)}
                >
                  <div className={cx(styles.wrapperImage)}>
                    <div className={cx(styles.leftGradientBg)} />
                    <Image
                      scale={1.05}
                      wrapperProps={{
                        className: cx(styles.image, imageClassName),
                      }}
                      src={img.src}
                      alt={img.alt}
                      layout="fill"
                      objectFit="cover"
                    />
                    <div className={cx(styles.rightGradientBg)} />
                    <div className={cx(styles.bgGradient)} />
                  </div>
                </div>
              );
            })}
          </div>
          <Icons
            name="chevron-right-outline"
            color="#F7EEDF"
            className={cx(`swiper-${galleryName}-next`, styles.nextChevronIcon)}
          />
          <div className={cx(styles.navigationWrapper)}>
            <div className={cx(styles.fraction)}>{`${formatFractionNumber(
              activeIndex + 1
            )} / ${formatFractionNumber(images.length)}`}</div>
            <div className={cx(styles.navigation)}>
              <Icons
                name="arrow-left-outline"
                color="#FFFFFF"
                className={cx(
                  `swiper-${galleryName}-prev`,
                  styles.prevArrowIcon
                )}
              />
              <Icons
                name="arrow-right-outline"
                color="#FFFFFF"
                className={cx(
                  `swiper-${galleryName}-next`,
                  styles.nextArrowIcon
                )}
              />
            </div>
          </div>
        </div>
        <div className={cx(styles.scrollbar, "swiper-scrollbar")} />
      </div>
    </div>
  );
}
